import { takeLatest, select, put, call, take } from 'redux-saga/effects';
import size from 'lodash/size';
import moment from 'moment';

import * as providerUpcomingAppointments from '../requests/virtual-appointments-upcomming-req';
import * as upcomingAppointments from '../requests/appointments-upcomming-req';
import * as historyAppointments from '../requests/appointments-history-req';
import * as cancelAppointment from '../requests/cancel-appointment-req';
import * as managePendingRequest from '../requests/manage-pending-request';
import { HEALTHBOARD_HOME } from '../../../constants/routes';
import { currentLocationSelector } from '../../common/selectors';
import {
  selectHistoryAppointmentList,
  selectUpcomingCount,
  selectUpcomingAppointmentList,
  selectProviderUpcomingCount,
  selectProviderUpcomingAppointmentList,
  selectProviderUpcomingFilterToday,
  selectProviderUpcomingFilterByTypeVisit,
} from '../selectors';
import {
  appointmentDetailsActions,
  APPOINTMENT_DETAILS_TYPES,
} from '../appointment-details';
import { hasProviderProfileSelector } from '../../context/selectors';
import {
  VIRTUAL_VISIT_EVENTS,
  VIRTUAL_VISIT_TYPE_CODE,
} from '../../../constants';

const cancelledStatuses = [
  VIRTUAL_VISIT_EVENTS.BREAK_VISIT,
  VIRTUAL_VISIT_EVENTS.DECLINE_VISIT,
];
const FIRST_REQUEST_LIMIT = 10;

function* fetchPatientAppointments(userId) {
  const location = yield select(currentLocationSelector);
  const counter = yield select(selectUpcomingCount);
  const upcomingList = yield select(selectUpcomingAppointmentList);
  const historyList = yield select(selectHistoryAppointmentList);
  const isHomeLocation = location.includes(`/${userId}/${HEALTHBOARD_HOME}`);

  yield put(appointmentDetailsActions.setAppointmentId(undefined));

  if (counter >= FIRST_REQUEST_LIMIT || isHomeLocation) {
    yield put(
      upcomingAppointments.paginationActions.send({
        limit: 1,
        id: userId,
        offset: size(upcomingList),
      }),
    );
  }

  yield put(
    historyAppointments.actions.send({
      silent: true,
      id: userId,
      offset: 0,
      limit:
        size(historyList) < FIRST_REQUEST_LIMIT
          ? FIRST_REQUEST_LIMIT
          : size(historyList),
    }),
  );
}

function* fetchProviderAppointments(userId) {
  const counter = yield select(selectProviderUpcomingCount);
  const upcomingList = yield select(selectProviderUpcomingAppointmentList);
  yield put(appointmentDetailsActions.setAppointmentId(undefined));

  if (counter >= FIRST_REQUEST_LIMIT) {
    const isFilterToday = yield select(selectProviderUpcomingFilterToday);
    const isFilterByTypeVisit = yield select(
      selectProviderUpcomingFilterByTypeVisit,
    );
    const todayDate = moment().format('YYYY-MM-DD');

    yield put(
      providerUpcomingAppointments.paginationActions.send({
        limit: 1,
        id: userId,
        ...(isFilterToday && { date: todayDate }),
        ...(isFilterByTypeVisit && {
          visit_type_code: VIRTUAL_VISIT_TYPE_CODE,
        }),
        offset: size(upcomingList),
      }),
    );
  }
}

/**
 *  PATIENT BEHAVIOUR
 * */

function* sliceAppointmentForPatient({ payload }) {
  const { userId } = payload;
  yield put(appointmentDetailsActions.setAppointmentId(undefined));
  yield call(fetchPatientAppointments, userId);
}

function* handleCancelOfficeVisit({ payload }) {
  const { userId } = payload;

  yield take(cancelAppointment.consts.SUCCEED);
  yield call(fetchPatientAppointments, userId);
}

/**
 *  PROVIDER BEHAVIOUR
 * */

function* cancelOfficeVisitAsProvider({ payload }) {
  const { userId } = payload;

  yield take(cancelAppointment.consts.SUCCEED);
  yield call(fetchProviderAppointments, userId);
}

function* sliceAppointmentAsProvider({ payload }) {
  const { userId } = payload;
  yield call(fetchProviderAppointments, userId);
}

function* declineAppointmentAsProvider({ payload }) {
  const { id, appointmentId, event } = payload;

  yield take(managePendingRequest.actions.succeed);
  if (cancelledStatuses.includes(event)) {
    yield put(
      appointmentDetailsActions.sliceUpcomingAppointment({
        id: appointmentId,
        userId: id,
      }),
    );
  }
}

export function* healthboardCancelSaga() {
  const isProvider = yield select(hasProviderProfileSelector);

  if (isProvider) {
    // As Provider
    yield takeLatest(
      cancelAppointment.consts.SEND,
      cancelOfficeVisitAsProvider,
    );

    yield takeLatest(
      managePendingRequest.actions.send,
      declineAppointmentAsProvider,
    );

    yield takeLatest(
      APPOINTMENT_DETAILS_TYPES.SLICE_UPCOMING_APPOINTMENT,
      sliceAppointmentAsProvider,
    );
  } else {
    // As Patient
    yield takeLatest(
      APPOINTMENT_DETAILS_TYPES.SLICE_UPCOMING_APPOINTMENT,
      sliceAppointmentForPatient,
    );

    yield takeLatest(cancelAppointment.consts.SEND, handleCancelOfficeVisit);
  }
}

export function* messagesAppointmentCancelSaga() {
  yield takeLatest(
    APPOINTMENT_DETAILS_TYPES.SLICE_UPCOMING_APPOINTMENT,
    sliceAppointmentForPatient,
  );

  yield takeLatest(
    managePendingRequest.actions.send,
    declineAppointmentAsProvider,
  );

  yield takeLatest(cancelAppointment.consts.SEND, handleCancelOfficeVisit);
}



// WEBPACK FOOTER //
// ./src/redux/healthboard/sagas/cancel-appointment-saga.js